export enum EFormStatus {
  active = 'active',
  inactive = 'inactive',
}

export enum EFormDisplayRule {
  everyConversation = 'everyConversation',
  everyConversationPrefilled = 'everyConversationPrefilled',
  oncePerGuestId = 'oncePerGuestId',
}

export enum EFormFieldMapping {
  contactFirstName = 'contactFirstName',
  contactLastName = 'contactLastName',
  contactEmail = 'contactEmail',
  contactPhone = 'contactPhone',
}

export enum EFormFieldDataType {
  string = 'string',
  email = 'email',
  phone = 'phone',
}

export enum EPreChatFormFieldStatus {
  active = 'active',
  inactive = 'inactive',
}

export enum EPreChatFormMetaChipVariant {
  inactive = 'inactive',
  pending = 'pending',
  shared = 'shared',
}

export enum EPreChatFormApiKeys {
  name = 'name',
  status = 'status',
  displayRule = 'displayRule',
  fields = 'fields',
  fieldMapping = 'fieldMapping',
  displayName = 'displayName',
  required = 'required',
  dataType = 'dataType',
  sortOrder = 'sortOrder',
}

export enum EPreChatFormKeys {
  id = 'id',
  name = 'name',
  displayRule = 'displayRule',
  status = 'status',
  assignedAgentCount = 'assignedAgentCount',
  assignedAgentIds = 'assignedAgentIds',
  fields = 'fields',
}

export enum EPreChatFormFieldKeys {
  id = 'id',
  fieldMapping = 'fieldMapping',
  displayName = 'displayName',
  required = 'required',
  dataType = 'dataType',
  sortOrder = 'sortOrder',
  status = 'status',
}

export type TPreChatFormField = {
  [EPreChatFormFieldKeys.id]?: string
  [EPreChatFormFieldKeys.fieldMapping]: EFormFieldMapping
  [EPreChatFormFieldKeys.displayName]: string
  [EPreChatFormFieldKeys.required]: boolean
  [EPreChatFormFieldKeys.dataType]: EFormFieldDataType
  [EPreChatFormFieldKeys.sortOrder]?: number
  [EPreChatFormFieldKeys.status]?: EPreChatFormFieldStatus
}

export type TPreChatForm = {
  [EPreChatFormKeys.id]?: string
  [EPreChatFormKeys.name]?: string
  [EPreChatFormKeys.displayRule]?: EFormDisplayRule
  [EPreChatFormKeys.status]?: EFormStatus
  [EPreChatFormKeys.assignedAgentCount]?: number
  [EPreChatFormKeys.assignedAgentIds]?: string[]
  [EPreChatFormKeys.fields]?: TPreChatFormField[]
}

// Allow-listed shape from GET /guest/agents/:agentId → chatSettings.form
// (see heyLibbyAgent guestAgentShape.ts). Active fields only; no status or
// org-scoped dashboard metadata.
export type TGuestPreChatFormField = {
  [EPreChatFormFieldKeys.id]: string
  [EPreChatFormFieldKeys.fieldMapping]: EFormFieldMapping
  [EPreChatFormFieldKeys.displayName]: string
  [EPreChatFormFieldKeys.required]: boolean
  [EPreChatFormFieldKeys.dataType]: EFormFieldDataType
  [EPreChatFormFieldKeys.sortOrder]: number
}

export type TGuestPreChatForm = {
  [EPreChatFormKeys.id]: string
  [EPreChatFormKeys.name]: string
  [EPreChatFormKeys.displayRule]: EFormDisplayRule
  [EPreChatFormKeys.fields]: TGuestPreChatFormField[]
}

export type TPreChatFormFieldLayoutSource = {
  [EPreChatFormFieldKeys.fieldMapping]?: EFormFieldMapping
  [EPreChatFormFieldKeys.sortOrder]: number
}

export type TPreChatFormFieldLayoutItem<T> =
  | { type: 'field'; field: T }
  | {
      type: 'nameRow'
      firstName: T
      lastName: T
    }

export type TFormSubmissionFieldForLayout = {
  displayName: string
  value: unknown
  sortOrder: number
  fieldMapping: EFormFieldMapping
}

export enum EPreChatFormFieldFormKeys {
  id = 'id',
  fieldMapping = EPreChatFormApiKeys.fieldMapping,
  displayName = EPreChatFormApiKeys.displayName,
  inForm = 'inForm',
  required = EPreChatFormApiKeys.required,
  dataType = EPreChatFormApiKeys.dataType,
}

export type TPreChatFormFieldFormValue = {
  [EPreChatFormFieldFormKeys.id]?: string
  [EPreChatFormFieldFormKeys.fieldMapping]: EFormFieldMapping
  [EPreChatFormFieldFormKeys.displayName]: string
  [EPreChatFormFieldFormKeys.inForm]: boolean
  [EPreChatFormFieldFormKeys.required]: boolean
  [EPreChatFormFieldFormKeys.dataType]: EFormFieldDataType
}

export enum ECreateEditPreChatFormKeys {
  formName = 'formName',
  displayRule = 'displayRule',
  isActive = 'isActive',
  fields = 'fields',
}

export type TCreateEditPreChatFormForm = {
  [ECreateEditPreChatFormKeys.formName]: string
  [ECreateEditPreChatFormKeys.displayRule]: EFormDisplayRule
  [ECreateEditPreChatFormKeys.isActive]: boolean
  [ECreateEditPreChatFormKeys.fields]: TPreChatFormFieldFormValue[]
}

export type TPreChatFormFieldPayload = {
  [EPreChatFormApiKeys.fieldMapping]: EFormFieldMapping
  [EPreChatFormApiKeys.displayName]: string
  [EPreChatFormApiKeys.required]?: boolean
  [EPreChatFormApiKeys.dataType]: EFormFieldDataType
  [EPreChatFormApiKeys.sortOrder]?: number
  [EPreChatFormFieldKeys.status]?: EPreChatFormFieldStatus
}

export type TCreatePreChatFormPayload = {
  [EPreChatFormApiKeys.name]: string
  [EPreChatFormApiKeys.status]?: EFormStatus
  [EPreChatFormApiKeys.displayRule]?: EFormDisplayRule
  [EPreChatFormApiKeys.fields]?: TPreChatFormFieldPayload[]
}

export type TUpdatePreChatFormPayload = Partial<TCreatePreChatFormPayload>

export enum EChoosePreChatFormDialogFormKeys {
  search = 'search',
  selectedFormId = 'selectedFormId',
}

export type TChoosePreChatFormDialogForm = {
  [EChoosePreChatFormDialogFormKeys.search]: string
  [EChoosePreChatFormDialogFormKeys.selectedFormId]: string
}
